import { useEffect } from 'react'
import * as maplibregl from 'maplibre-gl'
import type { Visitor } from '@/components/visitors/types'
import { LAYERS, SOURCES } from './visitorMapLayers'

interface UseMapHoverHighlightOptions {
  mapRef: React.RefObject<maplibregl.Map | null>
  hoveredId: string | null | undefined
  visitors: Visitor[]
}

// All layers that should be hidden when hovering a visitor
const REGULAR_LAYERS = [
  LAYERS.ACTIVE_CLUSTERS,
  LAYERS.ACTIVE_CLUSTER_COUNT,
  LAYERS.ACTIVE_MARKERS,
  LAYERS.LIVE_CLUSTER_PULSE,
  LAYERS.LIVE_CLUSTERS,
  LAYERS.LIVE_CLUSTER_COUNT,
  LAYERS.LIVE_PULSE_RING,
  LAYERS.LIVE_MARKERS,
]

/**
 * Handles hover highlighting on the visitor map.
 * When a visitor is hovered in the list, this hook:
 * - Hides all regular markers (clusters, individual markers, pulse rings)
 * - Shows a larger highlighted marker for the hovered visitor
 */
export function useMapHoverHighlight({
  mapRef,
  hoveredId,
  visitors,
}: UseMapHoverHighlightOptions) {
  useEffect(() => {
    const map = mapRef.current
    if (!map || !map.isStyleLoaded()) return

    const hoveredSource = map.getSource(SOURCES.HOVERED) as maplibregl.GeoJSONSource
    if (!hoveredSource) return

    try {
      // Clear hovered visitor source
      hoveredSource.setData({ type: 'FeatureCollection', features: [] })

      // Find the hovered visitor if hoveredId is set
      const hoveredVisitor = hoveredId ? visitors.find(v => v.id === hoveredId) : null

      if (hoveredVisitor?.location) {
        // Populate hovered visitor source with the hovered visitor
        hoveredSource.setData({
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              id: hoveredVisitor.id,
              geometry: {
                type: 'Point',
                coordinates: [hoveredVisitor.location.longitude, hoveredVisitor.location.latitude],
              },
              properties: {
                id: hoveredVisitor.id,
                name: hoveredVisitor.name,
                active: hoveredVisitor.active,
                live: hoveredVisitor.live,
              },
            },
          ],
        })

        // Hide all regular markers when hovering
        for (const layer of REGULAR_LAYERS) {
          map.setLayoutProperty(layer, 'visibility', 'none')
        }

        // Show hovered marker layers
        map.setLayoutProperty(LAYERS.HOVERED_PULSE, 'visibility', 'visible')
        map.setLayoutProperty(LAYERS.HOVERED_MARKER, 'visibility', 'visible')
      } else {
        // Restore visibility of regular markers
        for (const layer of REGULAR_LAYERS) {
          map.setLayoutProperty(layer, 'visibility', 'visible')
        }

        // Hide hovered marker layers
        map.setLayoutProperty(LAYERS.HOVERED_PULSE, 'visibility', 'none')
        map.setLayoutProperty(LAYERS.HOVERED_MARKER, 'visibility', 'none')
      }
    } catch {
      // Layers might not exist yet
    }
  }, [mapRef, hoveredId, visitors])
}
